import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { DeskToaster } from "@/lib/toast";
import "@/index.css";
import App from "@/App";
import { installPopstateGuard } from "@/lib/leaveRegistry";
import { AuthProvider } from "@/context/AuthContext";
import { BrandProvider } from "@/context/BrandContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LockProvider } from "@/context/LockContext";
import { PermissionsProvider } from "@/context/PermissionsContext";
import { ViewScopeProvider } from "@/context/ViewScopeContext";
import { FilePreviewProvider } from "@/context/FilePreviewContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Suppress the noisy but harmless ResizeObserver-loop warning that CRA's
// dev overlay treats as a fatal error (blocks clicks in dev). Safe to swallow
// per the CSSWG spec — it only signals that a re-layout was rescheduled.
if (typeof window !== "undefined") {
  const RO_MSG = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
  const handler = (e) => {
    const msg = e?.message || e?.reason?.message || "";
    if (msg && RO_MSG.test(msg)) {
      e.stopImmediatePropagation?.();
      e.preventDefault?.();
      return false;
    }
  };
  window.addEventListener("error", handler, true);
  window.addEventListener("unhandledrejection", handler, true);
  // iter358 (point 2) — plus aucun rechargement automatique : la carte « Mise à jour disponible » laisse l'utilisateur décider.
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false },
  },
});

installPopstateGuard();
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider>
          <BrandProvider>
            <AuthProvider>
              <PermissionsProvider>
                <ViewScopeProvider>
                  <LockProvider>
                    <FilePreviewProvider>
                      <App />
                      <DeskToaster />
                    </FilePreviewProvider>
                  </LockProvider>
                </ViewScopeProvider>
              </PermissionsProvider>
            </AuthProvider>
          </BrandProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);

// Register the service worker — required for PWA installability + agressive
// cache purge (spec 2026-08-02 fix icône jaune persistante). ``updateViaCache:
// "none"`` empêche le navigateur de mettre en cache /sw.js lui-même — sinon
// une nouvelle version du SW mettrait des semaines à être picked up.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => {
      // Force un check de mise à jour à chaque page load pour que la
      // nouvelle version du SW soit installée dès l'ouverture de l'app.
      try { reg.update(); } catch { /* ignore */ }
    }).catch((err) => {
      console.warn("Service worker registration failed:", err?.message || err);
    });
    // Quand le nouveau SW prend le contrôle, on hard-reload une fois pour
    // faire re-fetcher toutes les icônes fraîches.
    let reloaded = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

